import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import { useCallback, useMemo, useState } from 'react';
import FormResultsModalTitle from '@/modules/common/components/FormResultsModalTitle.tsx';
import Details from '@/modules/admin-dashboard/components/IncidentInfoModalTabs/Details.tsx';
// import History from '@/modules/admin-dashboard/components/IncidentInfoModalTabs/History.tsx';
import LinkedTasks from '@/modules/admin-dashboard/components/IncidentInfoModalTabs/LinkedTasks.tsx';
import { useUpdateIncidentMutation } from '@/modules/admin-dashboard/queries/incidents.ts';
import { ModalContentWrapper, TabItem, Tabs } from 'ui';
import { useOpen } from 'ui/hooks';
import { IncidentResultSchemaWithRel, IncidentTypeWithRelationsResponse } from 'compliance-api';

export interface IncidentInfoModalProps {
  incident: IncidentResultSchemaWithRel;
  incidentType: IncidentTypeWithRelationsResponse;
  defaultActiveTab?: TabNames;
  onClose: () => void;
  fromClientPage?: boolean;
}

export enum TabNames {
  Details = 'details',
  ResolveDetails = 'resolve_details',
  History = 'history',
  LinkedTasks = 'linked_tasks',
}

const IncidentInfoModal = ({
  incident,
  incidentType,
  onClose,
  defaultActiveTab = TabNames.Details,
  fromClientPage = false,
}: IncidentInfoModalProps) => {
  const [open, setOpen] = useState(true);
  const [editing, turnOnEditing, turnOffEditing] = useOpen();
  const [activeTab, setActiveTab] = useState<TabNames>(defaultActiveTab);

  const { mutateAsync, isLoading } = useUpdateIncidentMutation();

  const formik = useFormik<{ incident: IncidentResultSchemaWithRel }>({
    initialValues: { incident },
    onSubmit: async values => {
      await mutateAsync(values.incident);
      onClose();
    },
  });

  const resolveActiveTab =
    editing && activeTab !== TabNames.Details && activeTab !== TabNames.ResolveDetails
      ? TabNames.Details
      : activeTab;

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const tabs = useMemo<TabItem<TabNames>[]>(() => {
    let items: TabItem<TabNames>[] = [
      {
        name: TabNames.Details,
        label: `Report Form Details`,
        container: (
          <Details
            editing={editing}
            typeName={incidentType?.name}
            formName={incidentType?.form?.name}
            id={incidentType?.form?.reference_name}
            fromClientPage={fromClientPage}
          />
        ),
      },
    ];

    if (incidentType.completion_form?.name && incident.form_resolved_results) {
      items.push({
        name: TabNames.ResolveDetails,
        label: `Resolve Form Details`,
        container: (
          <Details
            editing={editing}
            typeName={incidentType?.name}
            formName={incidentType.completion_form?.name}
            id={incidentType.completion_form?.reference_name}
            fromClientPage={fromClientPage}
            isCompletionForm={true}
          />
        ),
      });
    }

    items = items.concat([
      // { name: TabNames.History, container: <History /> },
      {
        name: TabNames.LinkedTasks,
        container: <LinkedTasks incident={incident} onCloseModal={handleClose} />,
      },
    ]);

    return items;
  }, [editing, handleClose, incident, incidentType, fromClientPage]);

  return (
    <FormikProvider value={formik}>
      <ModalContentWrapper
        open={open}
        title={
          <FormResultsModalTitle
            editing={editing}
            onTurnOnEditing={turnOnEditing}
            name={incident.name}
            showPrint={activeTab === TabNames.Details || activeTab === TabNames.ResolveDetails}
            discardButton={{
              onClick: () => {
                formik.setValues(formik.initialValues);
                turnOffEditing();
              },
              disabled: isLoading,
            }}
            submitButton={{
              onClick: formik.handleSubmit,
              disabled: isLoading,
            }}
          />
        }
        onClose={handleClose}
        variant="right"
        classes={{
          children: 'p-0',
          dialogPanel: '!max-w-xl',
          titleWrapper: 'flex-1 [&>span]:w-full mt-[-2px] mr-3',
        }}
      >
        {
          <div className="px-2 sm:px-8 space-x-4 border-b pb-3 mt-4">
            {tabs.map(({ name, label }) => (
              <button
                key={name}
                className={classNames('text-[14px] leading-[22px] font-medium relative', {
                  'text-gray-500': resolveActiveTab !== name,
                  'rounded-underline': resolveActiveTab === name,
                })}
                onClick={setActiveTab.bind(null, name)}
                disabled={editing}
              >
                {typeof label === 'string'
                  ? capitalize(lowerCase(label))
                  : label || capitalize(lowerCase(name))}
              </button>
            ))}
          </div>
        }
        <Tabs items={tabs} activeTab={resolveActiveTab} />
      </ModalContentWrapper>
    </FormikProvider>
  );
};

export default IncidentInfoModal;
